import React from 'react';
import { CheckCircle2, Info, Lock } from 'lucide-react';
import { AppIcon } from './AppIcon';
import type { IconName } from '../assets/icons';

const listingFiles: { name: string; icon: IconName }[] = [
  { name: 'contract.pdf', icon: 'homeWorkdrivePdf' },
  { name: 'install_proof.jpg', icon: 'homeWorkdriveJpg' },
  { name: 'invoice.xlsx', icon: 'homeWorkdriveSheets' },
  { name: 'design_specifications.docx', icon: 'homeWorkdriveDoc' },
  { name: 'meeting_notes.txt', icon: 'homeWorkdriveTxt' },
  { name: 'project_timeline.pptx', icon: 'homeWorkdriveNoVisibility' },
];

const teamMembers = [
  { initials: 'SJ', name: 'Sarah Johnson', role: 'OWNER', avatar: 'bg-[#236DD9] text-white', badge: 'bg-[#236DD9] text-white' },
  { initials: 'MC', name: 'Mike Chen', role: 'EDITOR', avatar: 'bg-[#6366f1] text-white', badge: 'bg-slate-600 text-white' },
  { initials: 'LP', name: 'Lisa Park', role: 'VIEWER', avatar: 'bg-gray-200 text-gray-700', badge: 'bg-gray-200 text-gray-700' },
];

export const WorkDriveIllustration = () => {
  return (
    <div className="w-full bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-7 py-6 flex items-center justify-between" style={{ background: '#236DD9' }}>
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <AppIcon name="homeWorkdriveIcon" size={30} alt="WorkDrive" />
          </div>
          <div className="min-w-0">
            <div className="text-white font-bold text-2xl leading-tight">WorkDrive</div>
            <div className="text-white/85 text-base leading-tight truncate">Listing-first file management</div>
          </div>
        </div>
        <div className="bg-[#074693] text-white text-base px-5 py-2.5 rounded-full flex items-center gap-2 flex-shrink-0">
          <Lock className="w-4 h-4" />
          Secure
        </div>
      </div>

      {/* Body */}
      <div className="grid grid-cols-1 md:grid-cols-[0.9fr_1.1fr] min-h-[500px]">
        {/* Left — Folder Structure */}
        <div className="bg-[#f7f8fa] border-r border-gray-200 p-6">
          <div className="flex items-center gap-2.5 mb-5">
            <AppIcon name="homeWorkdriveFolderStructure" size={30} alt="Folder Structure" />
            <span className="text-sm font-semibold tracking-wide text-gray-500 uppercase">
              Folder Structure
            </span>
          </div>

          <div className="space-y-3 text-base">
            <div className="text-gray-500 pl-1">Downtown Office</div>

            <div className="flex items-center gap-2.5 pl-3 text-gray-700">
              <AppIcon name="homeWorkdriveFolder" size={18} alt="Team Alpha" />
              <span>Team Alpha</span>
            </div>

            <div className="ml-3 bg-white border border-blue-100 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-5">
                <AppIcon name="homeWorkdriveFolder" size={20} alt="123 Main St" />
                <span className="font-semibold text-gray-900 text-lg">123 Main St</span>
                <CheckCircle2 className="w-6 h-6 text-[#236DD9] ml-auto flex-shrink-0" />
              </div>

              <div className="space-y-3.5 pl-1">
                {listingFiles.map((file) => (
                  <div key={file.name} className="flex items-center gap-3 text-base text-gray-600">
                    <AppIcon name={file.icon} size={20} alt={file.name} className="flex-shrink-0" />
                    <span className="truncate">{file.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right — Secure sharing + Team permissions (scrollable) */}
        <div className="bg-white p-6 max-h-[500px] overflow-y-auto space-y-6">
          {/* Secure sharing */}
          <div className="border border-gray-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-5">
              <AppIcon name="homeWorkdriveSecureShare" size={24} alt="Secure sharing" />
              <span className="font-semibold text-gray-900 text-xl">Secure sharing</span>
            </div>

            <div className="rounded-lg border border-gray-100 bg-white pl-3 relative overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: '#236DD9' }} />

              <div className="p-5 space-y-5">
                <div className="text-sm font-semibold tracking-wide uppercase text-[#236DD9]">
                  Share Settings
                </div>

                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <AppIcon name="homeWorkdrivePasswordProtected" size={24} alt="Password protected" />
                    <span className="text-base text-gray-700">Password protected</span>
                  </div>
                  <div className="w-11 h-6 rounded-full relative flex-shrink-0" style={{ background: '#236DD9' }}>
                    <div className="absolute right-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow-sm" />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <AppIcon name="homeWorkdriveExpires" size={24} alt="Expires" />
                    <span className="text-base text-gray-700">Expires</span>
                  </div>
                  <div className="px-3.5 py-2 rounded-full bg-gray-50 border border-gray-200 text-base text-gray-800 flex-shrink-0">
                    Dec 31, 2024
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Team permissions */}
          <div className="border border-gray-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-5">
              <AppIcon name="homeWorkdriveTeamPermissions" size={24} alt="Team Permissions" />
              <span className="text-sm font-semibold tracking-wide text-gray-500 uppercase">
                Team Permissions
              </span>
            </div>

            <div className="divide-y divide-gray-100">
              {teamMembers.map((member) => (
                <div key={member.name} className="flex items-center gap-4 py-5 first:pt-0 last:pb-0">
                  <div
                    className={`w-11 h-11 rounded-full flex items-center justify-center text-base font-semibold flex-shrink-0 ${member.avatar}`}
                  >
                    {member.initials}
                  </div>
                  <span className="text-lg font-medium text-gray-900 flex-1 min-w-0 truncate">
                    {member.name}
                  </span>
                  <span
                    className={`text-sm font-semibold tracking-wide px-3.5 py-1.5 rounded-full flex-shrink-0 ${member.badge}`}
                  >
                    {member.role}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#f7f8fa] border-t border-gray-200 px-7 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2.5 text-base text-gray-500">
          <Info className="w-5 h-5 flex-shrink-0" />
          <span>4 items in folder • 1.2 GB used</span>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            className="px-5 py-2.5 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-lg"
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-5 py-2.5 text-base font-semibold text-white rounded-lg"
            style={{ background: '#236DD9' }}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};
